import type { useAuth } from "@itaas/core/auth/useAuth";
import {
  canAccessAdminSection,
  canAccessAdmissionsSection,
  canAccessBillingSection,
  canAccessClinicalSection,
  canAccessFinancialDashboard,
  canAccessImagingSection,
  canAccessInsuranceSection,
  canAccessLaboratorySection,
  canAccessNursingSection,
  canAccessHospitalizationSection,
  canAccessOperationalDashboard,
  canAccessPatientsSection,
  canAccessPharmacyModule,
  canAccessRhSection,
  canAccessStockSection,
  canManageInsurance,
} from "@/core/permissions";
import { routes } from "@/router/all_routes";
import { t } from "@/core/composition/lexicon";
import type { MenuCatalogSection, MenuCatalogItem } from "./menu-visibility";
import { MENU_DEMO_ADMIN_PATH } from "./menu-visibility";

type AuthLike = Pick<ReturnType<typeof useAuth>, "hasAnyPermission">;

export type { MenuCatalogItem };
export type { MenuCatalogSection };

/** Catalogue complet selon droits IAM — avant filtre démo. */
export function buildMenuCatalog(auth: AuthLike): MenuCatalogSection[] {
  const sections: MenuCatalogSection[] = [
    {
      id: "home",
      title: "Accueil",
      items: [
        { path: routes.tenantDashboard, label: "Tableau de bord" },
        { path: routes.directionHub, label: "Direction" },
        { path: routes.hospitalServicesHub, label: "Services hospitaliers" },
        ...(canAccessFinancialDashboard(auth)
          ? [{ path: routes.dashboardFinance, label: "Santé financière" }]
          : []),
        ...(canAccessOperationalDashboard(auth)
          ? [{ path: routes.dashboardOperations, label: "Performance opérationnelle" }]
          : []),
      ],
    },
    {
      id: "flow",
      title: "Accueil & flux",
      items: [
        ...(canAccessAdmissionsSection(auth)
          ? [
              { path: routes.admissions, label: "Admissions" },
              { path: routes.emergency, label: "Urgences" },
              { path: routes.ambulanceTransports, label: "Transports ambulance" },
            ]
          : []),
        { path: routes.appointments, label: "Rendez-vous" },
      ],
    },
  ];

  if (canAccessPatientsSection(auth)) {
    sections.push({
      id: "patients",
      title: "Patients",
      items: [
        { path: routes.patients, label: "Liste patients" },
        { path: routes.patientRecords, label: "Dossiers médicaux" },
      ],
    });
  }

  if (canAccessClinicalSection(auth)) {
    sections.push({
      id: "clinical",
      title: "Clinique",
      items: [
        { path: routes.consultations, label: "Consultations" },
        { path: routes.prescriptions, label: "Prescriptions" },
        { path: routes.surgery, label: "Chirurgie / bloc" },
      ],
    });
  }

  if (canAccessNursingSection(auth)) {
    sections.push({
      id: "nursing",
      title: "Soins infirmiers",
      items: [{ path: routes.nursingCare, label: "Constantes & tournées" }],
    });
  }

  if (canAccessHospitalizationSection(auth)) {
    sections.push({
      id: "hospitalization",
      title: "Hospitalisation",
      items: [
        { path: routes.hospitalization, label: "Lits & séjours" },
        { path: routes.patientConsumables, label: "Consommables patient" },
      ],
    });
  }

  if (canAccessPharmacyModule(auth)) {
    sections.push({
      id: "pharmacy",
      title: "Pharmacie",
      items: [
        { path: routes.pharmacyOrders, label: "Ordonnances à traiter" },
        { path: routes.pharmacyInvoice, label: "Vente comptoir" },
        { path: routes.pharmacyInvoiceHistory, label: "Historique ventes" },
        { path: routes.pharmacyStockArticles, label: "Articles médicaments" },
        { path: routes.pharmacyStockScan, label: "Scan code-barres" },
      ],
    });
  }

  if (canAccessLaboratorySection(auth)) {
    sections.push({
      id: "lab",
      title: "Laboratoire",
      items: [
        { path: routes.laboratory, label: "Analyses" },
        { path: routes.labResults, label: "Résultats" },
      ],
    });
  }

  if (canAccessImagingSection(auth)) {
    sections.push({
      id: "imaging",
      title: "Imagerie",
      items: [
        { path: routes.imaging, label: "Examens" },
        { path: routes.imagingReports, label: "Comptes-rendus" },
      ],
    });
  }

  if (canAccessInsuranceSection(auth)) {
    sections.push({
      id: "insurance",
      title: "Assurance médicale",
      items: [
        { path: routes.insuranceHome, label: "Tableau de bord" },
        { path: routes.insuranceInsurers, label: "Compagnies d'assurance" },
        { path: routes.insuranceCases, label: "Prises en charge" },
        ...(canManageInsurance(auth)
          ? [{ path: routes.insurancePartnerships, label: "Partenariats hub" }]
          : []),
      ],
    });
  }

  if (canAccessRhSection(auth)) {
    sections.push({
      id: "rh",
      title: t("section.rh"),
      items: [
        { path: routes.rhEmployees, label: t("rh.employees") },
        { path: routes.rhDepartments, label: t("rh.departments") },
        { path: routes.rhSchedules, label: t("rh.schedules") },
      ],
    });
  }

  if (canAccessStockSection(auth)) {
    sections.push({
      id: "supply",
      title: t("section.supply"),
      items: [
        { path: routes.supplyMedicinesArticles, label: "Médicaments — articles" },
        { path: routes.supplyMedicinesLevels, label: "Médicaments — niveaux" },
        { path: routes.supplyMedicinesReceipts, label: "Médicaments — réceptions" },
        { path: routes.supplyMedicinesReceiptsHistory, label: "Médicaments — historique réceptions" },
        { path: routes.supplyConsumablesArticles, label: "Consommables — articles" },
        { path: routes.supplyConsumablesLevels, label: "Consommables — niveaux" },
        { path: routes.supplyConsumablesReceipts, label: "Consommables — réceptions" },
        { path: routes.supplyConsumablesReceiptsHistory, label: "Consommables — historique réceptions" },
      ],
    });
  }

  if (canAccessPharmacyModule(auth) || canAccessBillingSection(auth)) {
    sections.push({
      id: "billing",
      title: t("section.billing"),
      items: [
        ...(canAccessBillingSection(auth)
          ? [
              {
                path: routes.billingAccounting,
                label: "Comptabilité — proforma & factures",
              },
              {
                path: routes.billingCashier,
                label: "Caisse — encaissements",
              },
            ]
          : []),
        { path: routes.billingInvoices, label: t("billing.invoices") },
      ],
    });
  }

  sections.push({
    id: "admin",
    title: "Administration",
    items: [
      { path: MENU_DEMO_ADMIN_PATH, label: "Personnaliser les menus" },
      ...(canAccessAdminSection(auth)
        ? [
            {
              path: routes.hospitolSetup,
              label: "Mise en route",
              badge: "live" as const,
            },
            { path: routes.adminUsers, label: "Utilisateurs" },
            { path: routes.adminSettings, label: "Paramètres établissement" },
            { path: routes.adminIntegrations, label: "Intégrations" },
          ]
        : []),
    ],
  });

  return sections;
}

export function filterMenuCatalog(
  catalog: MenuCatalogSection[],
  hiddenPaths: Set<string>,
): MenuCatalogSection[] {
  return catalog
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.path === MENU_DEMO_ADMIN_PATH || !hiddenPaths.has(item.path),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

export function catalogToSidebarSections(
  catalog: MenuCatalogSection[],
): Array<{ title: string; items: MenuCatalogItem[] }> {
  return catalog.map(({ title, items }) => ({ title, items }));
}
